/**
 * PHSIMS Design System — Color Tokens
 *
 * Cohesive palette derived from Zambian national identity.
 * All colors are chosen to work together harmoniously.
 *
 * RULES:
 *   1. NEVER use raw hex in components — import from here.
 *   2. Use MUI theme tokens (theme.palette.primary.main) where possible.
 *   3. Use these constants for domain-specific semantics (risk, severity, source).
 *   4. Background tints are 4% opacity of the accent — not unrelated hex values.
 *
 * Palette philosophy:
 *   - Primary (forest green) from Zambian flag for brand
 *   - Warm copper/amber from Zambian flag for urgency
 *   - Muted slate tones for neutrals (not pure black/white)
 *   - Indigo for intelligence/analytics (not generic blue)
 *   - Desaturated accent colors to avoid "candy shop" UIs
 */

// ─── Flag Colors (brand identity) ───────────────────────────────────────────

export const ZAMBIA = {
  forest:     '#0d5c2e',  // primary green (flag)
  forestLight:'#198a4b',  // lighter green
  forestDark: '#094a24',  // darker green
  copper:     '#c87533',  // flag orange, matured to copper
  copperLight:'#d4915a',  // lighter copper
  copperDark: '#a35e28',  // darker copper
  ember:      '#b42318',  // flag red, matured to ember
  emberLight: '#d4483d',  // lighter ember
  emberDark:  '#8c1b12',  // darker ember
} as const;

// ─── Brand marks (what the application actually wears) ──────────────────────

/**
 * The colours a user sees on the way in, recorded here because they were not
 * in this file and so could not be imported.
 *
 * `orange` is the PHSIMS wordmark and the login card's top ribbon. It appears
 * ~77 times across src/ as a raw hex, while ZAMBIA.copper — which this file
 * proposes for the same role — appears once. `ground` is the login page's
 * backdrop, commented there as "uniform deep forest green (Zambian theme)".
 *
 * Kept separate from ZAMBIA rather than replacing copper: existing components
 * reference these values directly, and quietly repointing a token they never
 * used would change nothing while implying it had. Reach for BRAND when
 * building something a user recognises as PHSIMS; reach for ZAMBIA for
 * derived chrome.
 */
export const BRAND = {
  orange:      '#ef7d00',  // PHSIMS wordmark, login ribbon
  orangeLight: '#ff9526',  // hover / active lift
  green:       '#0d5c2e',  // system name — same value as ZAMBIA.forest
  ground:      '#0d3322',  // login backdrop
} as const;

// ─── Semantic Colors (intent-based) ─────────────────────────────────────────

export const SEMANTIC = {
  /** Something needs attention but is not urgent */
  attention:    '#b45309',   // deep amber
  attentionBg:  '#fef3c7',   // amber tint (4%)

  /** Active danger / failure */
  danger:       '#b42318',   // ember red
  dangerBg:     '#fde8e6',   // ember tint

  /** Success / healthy / resolved */
  positive:     '#0d5c2e',   // forest green (matches brand)
  positiveBg:   '#e6f4ec',   // forest tint

  /** Informational — analytics, insights */
  insight:      '#3730a3',   // deep indigo
  insightBg:    '#e8e7f5',   // indigo tint

  /** Neutral / stable / no action */
  neutral:      '#475569',   // slate-600
  neutralBg:    '#f1f5f9',   // slate tint
} as const;

// ─── Risk Levels ────────────────────────────────────────────────────────────

export const RISK = {
  very_high: '#991b1b',     // dark red — unmistakably severe
  high:      '#b45309',     // deep amber — urgent but not critical
  moderate:  '#92400e',     // burnt sienna — cautionary
  low:       '#3f6212',     // olive green — mostly safe
  minimal:   '#475569',     // slate — unremarkable
  stable:    '#475569',     // slate

  very_high_bg: '#fde8e6',
  high_bg:      '#fef3c7',
  moderate_bg:  '#fff1e6',
  low_bg:       '#ecfccb',
  minimal_bg:   '#f1f5f9',
  stable_bg:    '#f1f5f9',
} as const;

// ─── Severity (for alerts/anomalies) ────────────────────────────────────────

export const SEVERITY = {
  critical:  '#991b1b',
  high:      '#b45309',
  medium:    '#92400e',
  moderate:  '#92400e',
  low:       '#1e4d8c',     // muted navy — not alarming
  minimal:   '#475569',

  critical_bg: '#fde8e6',
  high_bg:     '#fef3c7',
  medium_bg:   '#fff1e6',
  moderate_bg: '#fff1e6',
  low_bg:      '#e8eff7',
  minimal_bg:  '#f1f5f9',
} as const;

// ─── Trend Direction ────────────────────────────────────────────────────────

export const TREND = {
  rising:  '#991b1b',       // dark red — increase in disease is bad
  falling: '#0d5c2e',       // forest green — decrease is good
  stable:  '#475569',       // slate — unchanged
} as const;

// ─── Data Source Types ──────────────────────────────────────────────────────

export const SOURCE = {
  dhis2:   '#0d5c2e',       // forest green — primary source
  disa:    '#1e4d8c',       // navy blue — lab data
  weather: '#92400e',       // burnt sienna — climate/environment
  animal:  '#6b4226',       // brown — animal health
  upload:  '#5b21b6',       // deep violet — user-contributed
  default: '#475569',       // slate fallback
} as const;

// ─── One Health Domains ─────────────────────────────────────────────────────

export const DOMAIN = {
  human_health:         '#0d5c2e',
  animal_health:        '#6b4226',
  environmental_health: '#1e4d8c',
  food_safety:          '#92400e',
  lab:                  '#5b21b6',
  cross_domain:         '#475569',
} as const;

// ─── Chart Palette (sequential, not rainbow) ────────────────────────────────

export const CHART = {
  /** Ordered series colors — each is visually distinct but harmonious. */
  series: [
    '#0d5c2e',   // forest green
    '#1e4d8c',   // navy
    '#92400e',   // burnt sienna
    '#5b21b6',   // deep violet
    '#b45309',   // deep amber
    '#0e7490',   // teal
    '#6b4226',   // brown
    '#7e22ce',   // purple
    '#0369a1',   // steel blue
    '#a16207',   // dark gold
  ],

  /** Diverging scale — bad to good */
  diverging: {
    negative: '#991b1b',
    neutralLow: '#b45309',
    neutral: '#475569',
    neutralHigh: '#3f6212',
    positive: '#0d5c2e',
  },

  /** Sequential scale — light to dark (single hue) */
  sequential: [
    '#e6f4ec',
    '#b3dbca',
    '#80c2a8',
    '#4da986',
    '#198a4b',
    '#0d5c2e',
  ],
} as const;

// ─── Surfaces (backgrounds, borders, paper) ─────────────────────────────────

export const SURFACE = {
  page:    '#f8fafb',       // very subtle warm gray
  card:    '#ffffff',       // white
  raised:  '#ffffff',       // elevated card
  muted:   '#f1f5f9',       // slate-100
  divider: '#e2e8f0',       // slate-200
  border:  '#cbd5e1',       // slate-300
} as const;

// ─── Text ───────────────────────────────────────────────────────────────────

export const TEXT = {
  primary:   '#1e293b',     // slate-800
  secondary: '#64748b',     // slate-500
  tertiary:  '#94a3b8',     // slate-400
  inverse:   '#ffffff',
  link:      '#0d5c2e',     // forest green links
} as const;

// ─── Helper: Get risk color by level string ─────────────────────────────────

export function riskColor(level: string): string {
  return RISK[level as keyof typeof RISK] || RISK.minimal;
}

export function riskBgColor(level: string): string {
  const key = `${level}_bg` as keyof typeof RISK;
  return RISK[key] || RISK.minimal_bg;
}

export function severityColor(sev: string): string {
  const key = sev?.toLowerCase() as keyof typeof SEVERITY;
  return SEVERITY[key] || SEVERITY.minimal;
}

export function severityBgColor(sev: string): string {
  const key = `${sev?.toLowerCase()}_bg` as keyof typeof SEVERITY;
  return SEVERITY[key] || SEVERITY.minimal_bg;
}

export function trendColor(trend: string): string {
  const t = trend?.toLowerCase();
  if (t === 'up' || t === 'rising' || t === 'increasing') return TREND.rising;
  if (t === 'down' || t === 'falling' || t === 'decreasing') return TREND.falling;
  return TREND.stable;
}

export function sourceColor(sourceType: string): string {
  const key = sourceType?.toLowerCase() as keyof typeof SOURCE;
  return SOURCE[key] || SOURCE.default;
}
